import { ReactNode } from "react";
import { Button, ButtonProps } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import Input, { InputProps } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import Badge, { BadgeProps } from "@/components/ui/badge";
import Tabs, { tabsPropsType } from "@/components/controls/Tabs";
import SingleSelect, {
  SingleSelectProps
} from "@/components/controls/SingleSelect";
import {
  MultiSelect,
  MultiSelectProps
} from "@/components/controls/MultiSelect";
import { SwitchProps } from "@radix-ui/react-switch";
import { cn } from "@/utils/utils";
import { useTranslation } from "react-i18next";
import TextareaAutosize from "react-textarea-autosize";
import TextEditor from "@/components/controls/TextEditor/TextEditor";
import DateSelection from "@/components/controls/DateSelection";
import { toLocalDate, toDateOnlyISO } from "@/utils/datetime";

export type SettingItemActionType =
  | "button"
  | "chip"
  | "input"
  | "textarea"
  | "textEditor"
  | "switch"
  | "toggle"
  | "date"
  | "number"
  | "singleSelect"
  | "multiSelect";

interface ButtonWithIconProps extends ButtonProps {
  icon?: ReactNode;
  label: string;
}

interface TextareaProps {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

interface TextEditorActionProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

interface DateSelectionActionProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  availableFrom?: string;
  availableTo?: string;
}

export type ActionProps =
  | ButtonWithIconProps
  | SwitchProps
  | BadgeProps
  | InputProps
  | TextareaProps
  | TextEditorActionProps
  | DateSelectionActionProps
  | tabsPropsType
  | MultiSelectProps
  | SingleSelectProps;

export function useSettingItemAction() {
  const renderAction = (
    actionType: SettingItemActionType,
    props: ActionProps
  ): ReactNode => {
    const { t } = useTranslation();

    switch (actionType) {
      case "button":
        const buttonProps = props as ButtonWithIconProps;
        return (
          <Button
            {...buttonProps}
            onClick={buttonProps.onClick}
            variant={buttonProps.variant || "primary"}
            className="gap-1"
          >
            {buttonProps.icon && (
              <IconContainer size="5" color="current">
                {buttonProps.icon}
              </IconContainer>
            )}
            {buttonProps.label}
          </Button>
        );

      case "switch":
        const switchProps = props as SwitchProps;
        return (
          <Switch
            {...switchProps}
            checked={switchProps.checked}
            onCheckedChange={switchProps.onCheckedChange}
          />
        );

      case "chip":
        const chipProps = props as BadgeProps;
        return (
          <Badge
            textVariant={chipProps.textVariant || "bodySecondary"}
            {...chipProps}
          />
        );

      case "input":
        const inputProps = props as InputProps;
        return (
          <Input
            {...inputProps}
            // The spread already carries a caller's type; hardcoding it threw
            // away email, number and the rest.
            type={inputProps.type ?? "text"}
            value={inputProps.value}
            onChange={inputProps.onChange}
            placeholder={inputProps.placeholder}
            className={cn(
              "w-[280px] border-border-border2 h-9",
              inputProps.className
            )}
          />
        );

      case "date": {
        const dateProps = props as DateSelectionActionProps;
        return (
          <div className="w-[280px]">
            <DateSelection
              date={dateProps.value ? toLocalDate(dateProps.value) : null}
              onDateSelection={(newDate: Date) =>
                dateProps.onChange?.(toDateOnlyISO(newDate))
              }
              // Base rendered a native date input here, which the browser lets
              // the user empty; DateSelection only offers a clear control when
              // a caller supplies onClear.
              onClear={() => dateProps.onChange?.("")}
              placeholder={dateProps.placeholder}
              disabled={dateProps.disabled}
              availableFrom={dateProps.availableFrom ? toLocalDate(dateProps.availableFrom) : undefined}
              availableTo={dateProps.availableTo ? toLocalDate(dateProps.availableTo) : undefined}
              variant="form"
            />
          </div>
        );
      }

      case "textEditor": {
        const textEditorProps = props as TextEditorActionProps;
        return (
          <TextEditor
            value={textEditorProps.value}
            onChange={textEditorProps.onChange}
            onBlur={textEditorProps.onBlur}
            placeholder={textEditorProps.placeholder}
            disabled={textEditorProps.disabled}
            variant="form"
          />
        );
      }

      case "textarea":
        const textareaProps = props as TextareaProps;
        return (
          <TextareaAutosize
            value={textareaProps.value}
            onChange={textareaProps.onChange}
            onBlur={textareaProps.onBlur}
            placeholder={textareaProps.placeholder}
            disabled={textareaProps.disabled}
            minRows={1}
            maxRows={6}
            style={{ resize: "vertical" }}
            className={cn(
              "w-full bg-surface-field-default border-[0.5px] border-border-field-default rounded-lg px-2 py-[6px] text-base font-normal leading-[18px] outline-none placeholder:text-tertiary hover:border-border-field-hover focus:border-border-field-focus focus:bg-surface-focus-selected focus:shadow-field-focus shadow-field [&::-webkit-resizer]:hidden",
              textareaProps.disabled &&
                "opacity-50 cursor-not-allowed !shadow-none",
              textareaProps.className
            )}
          />
        );

      case "number":
        const numberProps = props as InputProps;
        return (
          <Input
            type="number"
            min={
              numberProps.min !== undefined ? numberProps.min.toString() : "0"
            }
            max={
              numberProps.max !== undefined
                ? numberProps.max.toString()
                : undefined
            }
            value={numberProps.value}
            className={cn(
              "w-[280px] border-border-border2 h-9",
              numberProps.className
            )}
          />
        );

      case "toggle":
        const toggleProps = props as tabsPropsType;
        return <Tabs size="lg" {...toggleProps} />;

      case "singleSelect":
        const singleSelectProps = props as SingleSelectProps;
        return (
          <SingleSelect
            {...singleSelectProps}
            options={singleSelectProps.options}
            selectedValue={singleSelectProps.selectedValue}
            onSelection={singleSelectProps.onSelection}
            formatter={singleSelectProps.formatter}
            className={
              singleSelectProps.className ||
              "text-start border border-border-border2 pl-0"
            }
            popoverTriggerClassName="w-[280px] min-h-8 h-9"
            variant="form"
          />
        );

      case "multiSelect":
        const multiSelectProps = props as MultiSelectProps;
        return (
          <MultiSelect
            selectedValues={multiSelectProps.selectedValues}
            options={multiSelectProps.options}
            onValueChange={multiSelectProps.onValueChange}
            showIconInChip={multiSelectProps.showIconInChip}
            modalPopover={multiSelectProps.modalPopover}
            placeholder={
              multiSelectProps.placeholder || t(`settings.inputPlaceHolder`)
            }
            maxCount={multiSelectProps.maxCount}
            placeholderClassName={
              multiSelectProps.placeholderClassName || "text-tertiary"
            }
            activeClassName={
              multiSelectProps.activeClassName || "!border-primary"
            }
            className={
              multiSelectProps.className ||
              "w-[280px] border-border-border2 min-h-8 h-9"
            }
          />
        );

      default:
        return null;
    }
  };

  return { renderAction };
}
